import {
  CameraOutlined,
  CarOutlined,
  CheckOutlined,
  ClockCircleOutlined,
  DollarOutlined,
  FileTextOutlined,
  LogoutOutlined,
  PlusOutlined,
  UserOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { DatePicker, Form, Input, InputNumber, Result, Select, Upload } from 'antd';
import type { UploadFile } from 'antd';
import dayjs from 'dayjs';
import { useState } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { PERMISSIONS } from '@tms/shared';

import { api, violationPhoto } from '@/api/client';
import { useApiMutation, useDictionaries } from '@/api/hooks';
import { useAuth } from '@/auth/AuthContext';

interface DriverOption {
  id: number;
  employee: {
    lastName: string;
    firstName: string;
    personnelNumber: string;
  };
}

interface VehicleOption {
  id: number;
  garageNumber: string;
  plateNumber: string | null;
}

/** Подпись поля с иконкой — единый приём для всех полей формы на этом экране. */
function FieldLabel({ icon, text }: { icon: ReactNode; text: string }) {
  return (
    <span className="tms-field-label">
      {icon}
      {text}
    </span>
  );
}

/**
 * Отдельный экран для сотрудника БД в поле — без бокового меню и таблиц
 * основного приложения: один сценарий на весь экран, крупные поля под
 * палец, фото сразу с камеры. Доступен по прямому адресу /field/violations
 * (и как ярлык PWA при установке — см. manifest.shortcuts в vite.config.ts).
 * Остальной функционал в поле не нужен и только мешал бы на маленьком экране.
 */
export function FieldViolationPage() {
  const { t } = useTranslation();
  const { user, can, logout } = useAuth();
  const [form] = Form.useForm();
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [justSubmitted, setJustSubmitted] = useState(false);
  const dictionaries = useDictionaries();

  const drivers = useQuery({
    queryKey: ['drivers-lookup'],
    queryFn: async () =>
      (await api.get('/drivers', { params: { pageSize: 200, isActive: true } })).data as {
        items: DriverOption[];
      },
  });

  const vehicles = useQuery({
    queryKey: ['vehicles-lookup'],
    queryFn: async () =>
      (await api.get('/vehicles', { params: { pageSize: 200, status: 'ACTIVE' } })).data as {
        items: VehicleOption[];
      },
  });

  const create = useApiMutation(
    async (values: Record<string, unknown>) => {
      const { data } = await api.post<{ id: number }>('/violations', {
        ...values,
        occurredAt: (values.occurredAt as dayjs.Dayjs).toISOString(),
      });

      const file = fileList[0]?.originFileObj;
      if (file) {
        await violationPhoto.upload(data.id, file);
      }

      return data;
    },
    { invalidate: [['violations'], ['driver-violations'], ['violation-standings']] },
  );

  const submit = (): void => {
    void form.validateFields().then((values) => {
      create.mutate(values, { onSuccess: () => setJustSubmitted(true) });
    });
  };

  const startNext = (): void => {
    form.resetFields();
    form.setFieldsValue({ occurredAt: dayjs() });
    setFileList([]);
    setJustSubmitted(false);
  };

  const violationTypes = dictionaries.data?.violationTypes ?? [];

  if (!can(PERMISSIONS.VIOLATION_MANAGE)) {
    return (
      <div className="tms-field-page">
        <FieldHeader user={user} onLogout={logout} />
        <div className="tms-field-body">
          <div className="tms-field-card">
            <Result
              status="403"
              title={t('Нет доступа')}
              subTitle={t('У вашей учётной записи нет прав на оформление нарушений')}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="tms-field-page">
      <FieldHeader user={user} onLogout={logout} />

      <div className="tms-field-body">
        <div className="tms-field-card">
          {justSubmitted ? (
            <div className="tms-field-success">
              <div className="tms-field-success-badge">
                <CheckOutlined />
              </div>
              <div className="tms-field-success-title">{t('Нарушение оформлено')}</div>
              <button type="button" className="tms-field-submit" onClick={startNext}>
                <PlusOutlined />
                {t('Оформить ещё одно')}
              </button>
            </div>
          ) : (
            <Form
              form={form}
              layout="vertical"
              className="tms-field-form"
              initialValues={{ occurredAt: dayjs() }}
              scrollToFirstError
            >
              <div className="tms-field-section-title">{t('Участники')}</div>

              <Form.Item
                name="driverId"
                label={<FieldLabel icon={<UserOutlined />} text={t('Водитель')} />}
                rules={[{ required: true, message: t('Обязательное поле') }]}
              >
                <Select
                  showSearch
                  optionFilterProp="label"
                  placeholder={t('Начните вводить фамилию')}
                  loading={drivers.isLoading}
                  options={drivers.data?.items.map((d) => ({
                    value: d.id,
                    label: `${d.employee.lastName} ${d.employee.firstName} (${d.employee.personnelNumber})`,
                  }))}
                />
              </Form.Item>

              <Form.Item
                name="vehicleId"
                label={<FieldLabel icon={<CarOutlined />} text={t('Техника')} />}
              >
                <Select
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  placeholder={t('Гаражный номер или госномер')}
                  loading={vehicles.isLoading}
                  options={vehicles.data?.items.map((v) => ({
                    value: v.id,
                    label: `${v.garageNumber}${v.plateNumber ? ` · ${v.plateNumber}` : ''}`,
                  }))}
                />
              </Form.Item>

              <div className="tms-field-section-title">{t('Нарушение')}</div>

              <Form.Item
                name="typeId"
                label={<FieldLabel icon={<WarningOutlined />} text={t('Вид нарушения')} />}
                rules={[{ required: true, message: t('Обязательное поле') }]}
              >
                <Select
                  showSearch
                  optionFilterProp="label"
                  onChange={(id: number) => {
                    const selected = violationTypes.find((item) => item.id === id);
                    form.setFieldsValue({
                      fineAmount: selected?.defaultFineAmount
                        ? Number(selected.defaultFineAmount)
                        : undefined,
                    });
                  }}
                  options={violationTypes.map((item) => ({ value: item.id, label: item.name }))}
                />
              </Form.Item>

              <Form.Item
                name="occurredAt"
                label={<FieldLabel icon={<ClockCircleOutlined />} text={t('Дата и время')} />}
                rules={[{ required: true, message: t('Обязательное поле') }]}
              >
                <DatePicker showTime format="DD.MM.YYYY HH:mm" style={{ width: '100%' }} />
              </Form.Item>

              <Form.Item
                name="fineAmount"
                label={<FieldLabel icon={<DollarOutlined />} text={t('Сумма штрафа')} />}
              >
                <InputNumber min={0} max={100_000_000} style={{ width: '100%' }} />
              </Form.Item>

              <Form.Item
                name="description"
                label={<FieldLabel icon={<FileTextOutlined />} text={t('Описание')} />}
              >
                <Input.TextArea rows={3} maxLength={400} />
              </Form.Item>

              <div className="tms-field-section-title">{t('Доказательство')}</div>

              <Form.Item>
                <Upload
                  listType="picture-card"
                  maxCount={1}
                  accept="image/jpeg,image/png,image/webp,image/heic"
                  capture="environment"
                  fileList={fileList}
                  className="tms-field-photo-upload"
                  beforeUpload={() => false}
                  onChange={({ fileList: next }) => setFileList(next.slice(-1))}
                >
                  {fileList.length === 0 && (
                    <span className="tms-field-photo-placeholder">
                      <CameraOutlined />
                      {t('Сфотографировать')}
                    </span>
                  )}
                </Upload>
              </Form.Item>

              <button
                type="button"
                className="tms-field-submit"
                disabled={create.isPending}
                onClick={submit}
              >
                {create.isPending ? t('Сохранение…') : t('Сохранить')}
              </button>
            </Form>
          )}
        </div>
      </div>
    </div>
  );
}

function FieldHeader({
  user,
  onLogout,
}: {
  user: { fullName: string; activeOffice: { name: string } } | null;
  onLogout: () => void;
}) {
  const { t } = useTranslation();
  const initial = user?.fullName?.trim().charAt(0).toUpperCase() ?? '?';

  return (
    <div className="tms-field-header">
      <div className="tms-field-header-brand">
        <img src="/favicon.svg" alt="" className="tms-field-header-logo" />
        <div className="tms-field-header-titles">
          <span className="tms-field-header-title">{t('Оформление нарушения')}</span>
          {user && <span className="tms-field-header-subtitle">{user.activeOffice.name}</span>}
        </div>
      </div>
      <div className="tms-field-header-user">
        <span className="tms-field-avatar">{initial}</span>
        <button
          type="button"
          className="tms-field-header-logout"
          aria-label={t('Выйти')}
          onClick={() => void onLogout()}
        >
          <LogoutOutlined />
        </button>
      </div>
    </div>
  );
}
